function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useRef } from 'react';
import { Button, Card, Column, Row, TextButton, Typography, CloseButton } from 'seagull';
import { LogoPicker } from "../../../components/LogoPicker/LogoPicker";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useScrollBorder } from "../../../hooks/useScrollBorder";
import { useBrandingActions } from "../BrandingActionsContext";
import { PreviewState } from "../ClientOnboarding.utils";
import { useCobBranding } from "../CobBrandingContext";
import { useCobConfig } from "../CobConfigContext";
import styles from "./BrandingControllers.module.scss";
import { ColorsLabel, SectionTitle } from "./BrandingControllers.shared";
import { ColorSelectorWithPopup } from "./ColorSelectorWithPopup";
import { PrimaryBackgroundColorPopup } from "./PrimaryBackgroundColorPopup";

/** In V2, progress bar uses brand color; sync progressBarComplete to buttonPrimary on every branding update. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function syncProgressBarToBrandColor(prev, next) {
  return _objectSpread(_objectSpread({}, next), {}, {
    progressBarComplete: next.buttonPrimary
  });
}
export function BrandingPanelV2() {
  const {
    branding,
    logoImage,
    errors,
    isDirty,
    isValid,
    setBranding: setBrandingContext,
    setLogo,
    revertToDefault,
    saveLogoAndBranding
  } = useCobBranding();
  const setBranding = useCallback(update => {
    setBrandingContext(prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      return syncProgressBarToBrandColor(prev, next);
    });
  }, [setBrandingContext]);
  const {
    SET_UP_BRAND_CLIENT_PREVIEW = false
  } = useFeatureFlags();
  const {
    originalPreviewState
  } = useCobConfig();
  const {
    onToggleBrandSetup
  } = useBrandingActions();
  function handleCloseClick() {
    onToggleBrandSetup();
  }
  const contentRef = useRef(null);
  const {
    headerClassName,
    footerClassName
  } = useScrollBorder({
    contentRef,
    headerBaseClassName: styles.headerSection,
    footerBaseClassName: styles.saveSection
  }, []);
  return /*#__PURE__*/_jsxs(Card, {
    className: styles.brandingCardNew,
    children: [SET_UP_BRAND_CLIENT_PREVIEW ? /*#__PURE__*/_jsxs(Row, {
      alignItems: "center",
      justifyContent: "space-between",
      className: headerClassName,
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        variant: "paragraph1",
        children: 'Customize brand'
      }), originalPreviewState !== PreviewState.SetupBrand && /*#__PURE__*/_jsx(CloseButton, {
        skin: "standard",
        onClick: handleCloseClick,
        testId: "close-branding-setup-btn"
      })]
    }) : /*#__PURE__*/_jsx("div", {
      className: headerClassName,
      children: /*#__PURE__*/_jsx(SectionTitle, {
        children: 'Upload logo'
      })
    }), /*#__PURE__*/_jsxs("div", {
      ref: contentRef,
      className: styles.scrollableContent,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.logoSection,
        children: /*#__PURE__*/_jsx(Column, {
          gap: 12,
          children: /*#__PURE__*/_jsx(LogoPicker, {
            isNewUI: true,
            testId: "upload-logo",
            image: logoImage,
            onChange: setLogo
          })
        })
      }), /*#__PURE__*/_jsx("div", {
        className: styles.colorsSection,
        children: /*#__PURE__*/_jsxs(Column, {
          gap: 24,
          children: [/*#__PURE__*/_jsxs(Row, {
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(ColorsLabel, {
              children: "Brand color"
            }), /*#__PURE__*/_jsx(Row, {
              gap: 6,
              children: /*#__PURE__*/_jsx(ColorSelectorWithPopup, {
                branding: branding,
                hasError: errors.has('buttonPrimary'),
                colorKey: "buttonPrimary",
                onChange: setBranding
              })
            })]
          }), /*#__PURE__*/_jsxs(Row, {
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(ColorsLabel, {
              children: "Primary background"
            }), /*#__PURE__*/_jsx(Row, {
              gap: 6,
              children: /*#__PURE__*/_jsx(PrimaryBackgroundColorPopup, {
                branding: branding,
                hasError: errors.has('backgroundGradientStart') || errors.has('backgroundGradientEnd'),
                onChange: setBranding
              })
            })]
          }), /*#__PURE__*/_jsxs(Row, {
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(ColorsLabel, {
              children: "Secondary background"
            }), /*#__PURE__*/_jsx(Row, {
              gap: 6,
              children: /*#__PURE__*/_jsx(ColorSelectorWithPopup, {
                branding: branding,
                hasError: errors.has('backgroundStandard'),
                colorKey: "backgroundStandard",
                onChange: setBranding
              })
            })]
          })]
        })
      })]
    }), /*#__PURE__*/_jsxs(Column, {
      gap: 12,
      alignItems: "center",
      className: footerClassName,
      children: [/*#__PURE__*/_jsx(Button, {
        skin: "primaryOutline",
        disabled: !isDirty || !isValid,
        testId: "save-colors",
        tooltip: {
          placement: 'top',
          title: !isDirty ? 'Nothing to save' : !isValid ? 'One of the fields is invalid' : ''
        },
        onClick: saveLogoAndBranding,
        className: styles.saveBtn,
        children: "Save changes"
      }), /*#__PURE__*/_jsx(TextButton, {
        testId: "undo-color-changes",
        variant: "paragraph2",
        skin: "primary",
        underline: "none",
        weight: "normal",
        onClick: revertToDefault,
        children: "Revert to default colors"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "paragraph3",
        skin: "secondary",
        className: styles.disclaimer,
        children: "Changes saved here will apply to all of your proposals."
      })]
    })]
  });
}